import { Store } from "../../../__generated__/graphql";
import { PickupTimeFilter } from "../hooks/use-pickup-time-filter";
import {
  canProductExpose,
  canPackExpose,
  getExposeType,
} from "../../../utils/store-expose";
import {
  getDayOfWeekIndex,
  getTomorrowDayOfWeekIndex,
} from "../../../utils/reserve-time";

/**
 * 시간 문자열을 분 단위로 변환 (예: "17:30" -> 1050)
 */
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * 매장 필터 맵 (ALL, AVAILABLE, PICKUP_NOW)
 */
export const STORE_FILTER: Record<string, (store: Store | any) => boolean> = {
  ALL: () => true,
  AVAILABLE: (store: Store | any) => {
    if (canProductExpose(store)) {
      return true;
    }
    if (canPackExpose(store) && store.pack) {
      const exposeType = getExposeType(store.pack);
      if (store.pack.currentCount > 0 && exposeType !== "X") {
        return true;
      }
    }
    return false;
  },
  PICKUP_NOW: (store: Store | any) => {
    // 실시간 판매만 필터링 (바로픽업 가능한 매장)
    return canProductExpose(store);
  },
};

/**
 * localStorage에서 저장된 필터 읽기
 */
export const processAvailableFilter = ():
  | "ALL"
  | "AVAILABLE"
  | "PICKUP_NOW" => {
  const availableFilter = localStorage.getItem("availableFilter");
  if (availableFilter === "ALL") {
    return "ALL";
  }
  if (availableFilter === "AVAILABLE") {
    return "AVAILABLE";
  }
  if (availableFilter === "PICKUP_NOW") {
    return "PICKUP_NOW";
  }
  return "AVAILABLE";
};

/**
 * 픽업 시간 필터로 매장 필터링
 */
export const filterByPickupTime = (
  store: Store | null,
  pickupTimeFilter: PickupTimeFilter | null
): boolean => {
  if (!store || !pickupTimeFilter) {
    return true; // 필터가 설정되지 않았으면 모든 매장 표시
  }

  const filterStartMinutes = timeToMinutes(pickupTimeFilter.startTime);
  const filterEndMinutes = timeToMinutes(pickupTimeFilter.endTime);

  // 실시간 판매 중인 경우 (StoreCardForTimesail)
  if (canProductExpose(store)) {
    if (!store.closingHours) {
      return false; // 마감시간이 없으면 필터링 제외
    }

    // 실시간 판매는 지금부터 closingHours까지 언제든 가능
    // 필터 시간 범위(예: 17:00 ~ 22:00)와 매장 영업 시간(지금 ~ closingHours)이 겹치면 표시
    const closingMinutes = timeToMinutes(store.closingHours);
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // 필터 시작 시간이 closingHours 이전이고, 필터 종료 시간이 현재 시간 이후인 경우
    // 즉, 필터 시간 범위와 매장 영업 시간이 겹치는 경우
    return (
      filterStartMinutes < closingMinutes && filterEndMinutes > currentMinutes
    );
  }

  // 예약 팩인 경우 (StoreCardForPack)
  if (canPackExpose(store) && store.pack) {
    if (!store.reserveTime) {
      return false; // reserveTime이 없으면 필터링 제외
    }

    const exposeType = getExposeType(store.pack);

    // 오늘 예약 가능한 경우
    if (exposeType === "오늘") {
      const dayOfWeek = getDayOfWeekIndex(new Date());
      const reserveTimeForDay = store.reserveTime[dayOfWeek];

      if (!reserveTimeForDay || !reserveTimeForDay.enabled) {
        return false;
      }

      const storeStartMinutes = timeToMinutes(reserveTimeForDay.start);
      const storeEndMinutes = timeToMinutes(reserveTimeForDay.end);

      // 선택한 시간대와 매장의 픽업 시간이 겹치는지 확인
      return (
        storeStartMinutes < filterEndMinutes &&
        storeEndMinutes > filterStartMinutes
      );
    }

    // 내일 예약 가능한 경우
    if (exposeType === "내일") {
      const tomorrowDayOfWeek = getTomorrowDayOfWeekIndex();
      const reserveTimeForTomorrow = store.reserveTime[tomorrowDayOfWeek];

      if (!reserveTimeForTomorrow || !reserveTimeForTomorrow.enabled) {
        return false;
      }

      const storeStartMinutes = timeToMinutes(reserveTimeForTomorrow.start);
      const storeEndMinutes = timeToMinutes(reserveTimeForTomorrow.end);

      // 선택한 시간대와 매장의 픽업 시간이 겹치는지 확인
      return (
        storeStartMinutes < filterEndMinutes &&
        storeEndMinutes > filterStartMinutes
      );
    }

    // exposeType이 "X"인 경우 필터링 제외
    return false;
  }

  // 실시간도 아니고 예약 팩도 아닌 경우 필터링 제외
  return false;
};
